import yousignConfig from "@config/yousign";
import { useEffect, useRef } from "react";

declare global {
  interface Window {
    Yousign?: new (config: { signatureLink: string; iframeContainerId: string; isSandbox: boolean }) => {
      onStarted: (cb: () => void) => void;
      onSuccess: (cb: () => void) => void;
      onError: (cb: (error: unknown) => void) => void;
      onDeclined: (cb: () => void) => void;
      onSignatureDone: (cb: () => void) => void;
      removeMessageListener: () => void;
    };
  }
}

const SDK_URL = "https://cdn.yousign.tech/iframe-sdk-1.6.0.min.js";
const SCRIPT_ID = "yousign-sdk";

function loadSdk(): Promise<void> {
  if (document.getElementById(SCRIPT_ID)) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SDK_URL;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Yousign SDK"));
    document.head.appendChild(script);
  });
}

interface UseYousignIframeParams {
  signatureLink: string | null;
  containerId: string;
  onStarted?: () => void;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
  onDeclined?: () => void;
  onSignatureDone?: () => void;
}

export function useYousignIframe({
  signatureLink,
  containerId,
  onStarted,
  onSuccess,
  onError,
  onDeclined,
  onSignatureDone,
}: UseYousignIframeParams) {
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!signatureLink) return;

    let cancelled = false;

    loadSdk().then(() => {
      if (cancelled || !window.Yousign) return;

      const yousign = new window.Yousign({
        signatureLink,
        iframeContainerId: containerId,
        isSandbox: yousignConfig.isSandbox,
      });

      if (onStarted) yousign.onStarted(onStarted);
      if (onSuccess) yousign.onSuccess(onSuccess);
      if (onError) yousign.onError(onError);
      if (onDeclined) yousign.onDeclined(onDeclined);
      if (onSignatureDone) yousign.onSignatureDone(onSignatureDone);

      cleanupRef.current = () => yousign.removeMessageListener();
    });

    return () => {
      cancelled = true;
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
    // Omitting callback props (onStarted, onSuccess, etc.) — SDK is initialized once per signatureLink/containerId pair; re-creating the iframe on callback change would break the signing flow
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signatureLink, containerId]);
}
